export function getUserRole(account = {}) {
  return (
    account.role_name ||
    account.user?.role_name ||
    account.UserDetails?.role_name ||
    account.org_member?.role_name ||
    account.role?.role ||
    account.user?.role?.role ||
    account.UserDetails?.role?.role ||
    account.org_member?.role?.role ||
    account.role ||
    account.user?.role ||
    account.UserDetails?.role ||
    account.org_member?.role ||
    ''
  );
}

export function getUserAccountId(account = {}) {
  const directId =
    account.user?.id ||
    account.user?.data?.id ||
    account.UserDetails?.id ||
    account.UserDetails?.data?.id;

  if (directId != null) return directId;

  // Older persisted login payloads omitted UserAccount.id, but the JWT stores
  // it as `_id`. Decode only to recover identity; authentication still remains
  // the backend's responsibility.
  try {
    const rawToken = account.token || (typeof window !== 'undefined' ? window.localStorage.getItem('token') : null);
    const token = String(rawToken || '').replace(/^"|"$/g, '');
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const payload = JSON.parse(window.atob(padded));
    return payload?._id || payload?.user_id || payload?.id || null;
  } catch (_) {
    return null;
  }
}
 
export function getAssignedAgents(account = {}) {
  const orgMember = account.org_member || {};
  const user = account.user || account.UserDetails || {};
  const pendingAllocations =
    account.pending_license_allocation?.allocations ||
    orgMember.pending_license_allocation?.allocations ||
    user.pending_license_allocation?.allocations ||
    {};
 
  const legacyAssignedAgents = {
    ...(isPositiveAgentSeat(orgMember.license_allocated_chatwidget || orgMember.license_allocated_chat_widget) ? { chat_widget: 1 } : {}),
    ...(isPositiveAgentSeat(orgMember.license_allocated_document_agent) ? { document_agent: 1 } : {}),
    ...(isPositiveAgentSeat(orgMember.license_allocated_shipping_document_agent) ? { shipping_document_agent: 1 } : {}),
    ...(isPositiveAgentSeat(orgMember.license_allocated_spreadsheet_ai) ? { spreadsheet_ai: 1 } : {}),
    ...(isPositiveAgentSeat(orgMember.license_allocated_tax_agent) ? { tax_agent: 1 } : {}),
    ...(isPositiveAgentSeat(orgMember.license_allocated_labour_law_advisor) ? { labour_law_advisor: 1 } : {}),
    ...(isPositiveAgentSeat(orgMember.license_allocated_compliance_agent) ? { compliance_agent: 1 } : {}),
  };
 
  return {
    ...legacyAssignedAgents,
    ...pendingAllocations,
    ...(account.org_member?.assigned_agents || {}),
    ...(account.user?.assigned_agents || {}),
    ...(account.UserDetails?.assigned_agents || {}),
    ...(account.assigned_agents || {}),
  };
}
 
export function getAssignedAgentDetails(account = {}) {
  const details =
    account.assigned_agent_details ||
    account.org_member?.assigned_agent_details ||
    account.agent_details ||
    [];
 
  return Array.isArray(details) ? details : [];
}
 
const PLATFORM_AGENT_CODES = new Set(['image_i']);
 
function getAgentCode(agent = {}) {
  return agent?.code || agent?.agent_code;
}
 
function hasAssignedAgentMetadata(account = {}) {
  const assignedAgents = getAssignedAgents(account);
 
  return (
    Object.keys(assignedAgents).length > 0 ||
    getAssignedAgentDetails(account).length > 0
  );
}
 
function hasAllocatedReports(account = {}) {
  const reports =
    account.user?.reports ||
    account.reports ||
    account.org_member?.reports ||
    account.UserDetails?.reports;
 
  return Array.isArray(reports) && reports.length > 0;
}
 
function getAgentSeatCount(agent = {}) {
  return Number(
    agent.seats_purchased ??
    agent.seats_total ??
    agent.totalSeats ??
    agent.seats ??
    0
  ) || 0;
}
 
function getOrgAgentPools(account = {}) {
  return Array.isArray(account.org_agent_seat_pools)
    ? account.org_agent_seat_pools
    : [];
}
 
export function getAgentRoutePath(account = {}, agentCode) {
  const detail = getAssignedAgentDetails(account).find(
    (agent) => (agent?.code || agent?.agent_code) === agentCode
  );
 
  const orgAgent = getOrgAgentPools(account).find(
    (agent) => (agent?.code || agent?.agent_code) === agentCode
  );
 
  return (
    detail?.route_path ||
    detail?.route ||
    detail?.url ||
    orgAgent?.route_path ||
    orgAgent?.route ||
    orgAgent?.url ||
    (agentCode ? `/imagei/agents/custom/${encodeURIComponent(agentCode)}` : null)
  );
}
 
export function getEnabledOrgAgentPools(account = {}) {
  return getOrgAgentPools(account).filter((agent) => {
    const code = getAgentCode(agent);
 
    return (
      code &&
      agent.is_active !== false &&
      getAgentSeatCount(agent) > 0
    );
  });
}
 
function getEnabledNonPlatformOrgAgentPools(account = {}) {
  return getEnabledOrgAgentPools(account).filter(
    (agent) => !PLATFORM_AGENT_CODES.has(getAgentCode(agent))
  );
}
 
function orgPoolHasImageI(account = {}) {
  return getEnabledOrgAgentPools(account).some(
    (agent) => getAgentCode(agent) === 'image_i'
  );
}
 
export function getSingleOrgAgentPath(account = {}) {
  if (getUserRole(account) !== 'Super Admin') return null;
  if (orgPoolHasImageI(account)) return null;
 
  const enabledOrgAgents = getEnabledNonPlatformOrgAgentPools(account);
  const totalOrgAgentSeats = enabledOrgAgents.reduce(
    (total, agent) => total + getAgentSeatCount(agent),
    0
  );
 
  if (totalOrgAgentSeats !== 1 || enabledOrgAgents.length !== 1) return null;
 
  const agentCode = getAgentCode(enabledOrgAgents[0]);
  return getAgentRoutePath(account, agentCode);
}
 
export function getEnabledAssignedAgentCodes(account = {}) {
  const assignedAgents = getAssignedAgents(account);
 
  const enabledAgentCodes = new Set(
    Object.keys(assignedAgents).filter((agentCode) =>
      isPositiveAgentSeat(assignedAgents[agentCode])
    )
  );
 
  return Array.from(enabledAgentCodes);
}
 
export function getSingleAssignedAgentPath(account = {}) {
  const enabledAgents = getEnabledAssignedAgentCodes(account);
 
  if (enabledAgents.length !== 1) return null;
 
  return getAgentRoutePath(account, enabledAgents[0]);
}
 
export function getSingleAssignedAgentWithoutImageIPath(account = {}) {
  if (hasAssignedAgentMetadata(account)) {
    return getSingleAssignedAgentPath(account);
  }
 
  const singleOrgAgentPath = getSingleOrgAgentPath(account);
  if (singleOrgAgentPath) return singleOrgAgentPath;
 
  return getSingleAssignedAgentPath(account);
}
 
export function getAgentLandingPath(account = {}) {
  return getSingleAssignedAgentPath(account) || '/imagei/agents';
}
 
export function hasImageILicense(account = {}) {
  if (isOwnerAccount(account)) return true;
 
  const assignedAgents = getAssignedAgents(account);
 
  if (hasAssignedAgentMetadata(account)) {
    return isPositiveAgentSeat(assignedAgents.image_i);
  }
 
  if (
    getUserRole(account) === 'Super Admin' &&
    getEnabledOrgAgentPools(account).length > 0
  ) {
    return orgPoolHasImageI(account);
  }
 
  return (
    isPositiveAgentSeat(assignedAgents.image_i) ||
    hasAllocatedReports(account)
  );
}
 
export function hasAnyAssignedAgent(account = {}) {
  if (getEnabledAssignedAgentCodes(account).length > 0) return true;
 
  if (getUserRole(account) === 'Super Admin') {
    return getEnabledNonPlatformOrgAgentPools(account).length > 0;
  }
 
  return false;
}
 
export function isAgentOnlyAccount(account = {}) {
  if (!hasAnyAssignedAgent(account)) return false;
  if (hasImageILicense(account)) return false;
 
  return !hasAllocatedReports(account);
}
 
export function isOwnerAccount(account = {}) {
  const email =
    account.user?.email ||
    account.UserDetails?.email ||
    account.org_member?.email ||
    '';
 
  return getUserRole(account) === 'Owner' || email.toLowerCase() === 'owner@test.com';
}
 
export function canManageUsers(account = {}) {
  return getUserRole(account) === 'Super Admin';
}
 
export function canAllocateUsers(account = {}) {
  if (getUserRole(account) !== 'Super Admin') return false;
 
  return getEnabledOrgAgentPools(account).some(
    (agent) => getAgentSeatCount(agent) > 1
  );
}
 
export function isPositiveAgentSeat(value) {
  if (value === true) return true;
 
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || Number(value) > 0;
  }
 
  return Number(value) > 0;
}
 
export function hasAgentLicense(account = {}, agentCode, legacyKey) {
  if (isOwnerAccount(account)) return true;
 
  const assignedAgents = getAssignedAgents(account);
 
  if (hasAssignedAgentMetadata(account)) {
    return isPositiveAgentSeat(assignedAgents[agentCode]);
  }
 
  if (getUserRole(account) === 'Super Admin') {
    const orgAgents = getOrgAgentPools(account);
 
    const orgAgent = orgAgents.find(
      (agent) => (agent?.code || agent?.agent_code) === agentCode
    );
 
    if (
      orgAgent &&
      orgAgent.is_active !== false &&
      getAgentSeatCount(orgAgent) > 0
    ) {
      return true;
    }
  }
 
  if (isPositiveAgentSeat(assignedAgents[agentCode])) return true;
  if (legacyKey && account.org_member?.[legacyKey] === true) return true;
 
  return false;
}
